/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team
 * <mailto:radmanesh@gmail.com Arman Radmanesh>
 * <mailto:salarirad@gmail.com Mostafa Salari Rad>
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAlert } from '../../context/AlertContext';
import { db } from '../../firebase';
import ParticipantsPanel from '../components/ParticipantsPanel';
import ProjectHealthPanel from '../components/ProjectHealthPanel';
import ProjectDistributionPanel from '../components/ProjectDistributionPanel';
import InsightsPanel from '../components/InsightsPanel';
import ProjectSettingsPanel from '../components/ProjectSettingsPanel';
import HealthChat from '../components/HealthChat';
import Breadcrumbs from '../components/Breadcrumbs';
import './admin.css';

// Settings: study details and identifiers. Data: Google Health retrieval (daily and manual fetches).
// Results: charts of the fetched data. Explore with AI: Gemini chat over the data.
const TABS = ['Settings', 'Participants', 'Distribution', 'Data', 'Results', 'Explore with AI'];

const ShowProject = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tabValue, setTabValue] = useState(1); // Participants
  const [isLoading, setIsLoading] = useState(false);
  const { addAlert } = useAlert();

  useEffect(() => onSnapshot(doc(db, 'projects', projectId), (snap) => {
    if (snap.exists()) {
      setProject({ ...snap.data(), id: snap.id });
    } else {
      addAlert('error', `Project with id: ${projectId} could not be found!`);
      setProject(null);
    }
  }), [addAlert, projectId]);

  return (
    <div className="ja-root">
      <div className="ja-page">

        <div className="ja-page-header">
          <div>
            <Breadcrumbs items={[{ label: 'Dashboard', to: '/admin' }, { label: 'Project' }]} />
            <h1 className="ja-page-title">
              <strong>{project?.name ?? '…'}</strong>
            </h1>
            {project?.description && (
              <p className="ja-page-subtitle">{project.description}</p>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="ja-progress">
            <div className="ja-progress-bar" />
          </div>
        )}

        <div className="ja-tab-bar" role="tablist">
          {TABS.map((label, i) => (
            <button
              key={label}
              role="tab"
              aria-selected={tabValue === i}
              className={`ja-tab${tabValue === i ? ' active' : ''}`}
              onClick={() => setTabValue(i)}
            >
              {label}
            </button>
          ))}
        </div>

        {project && (
          <>
            {TABS[tabValue] === 'Settings' && (
              <ProjectSettingsPanel project={project} onOpenTab={(name) => setTabValue(TABS.indexOf(name))} />
            )}
            {TABS[tabValue] === 'Participants' && (
              <ParticipantsPanel project={project} isLoading={isLoading} setIsLoading={setIsLoading} />
            )}
            {TABS[tabValue] === 'Distribution' && <ProjectDistributionPanel projectId={projectId} />}
            {TABS[tabValue] === 'Data' && <ProjectHealthPanel project={project} projectId={projectId} />}
            {TABS[tabValue] === 'Results' && (
              <InsightsPanel project={project} isLoading={isLoading} setIsLoading={setIsLoading} />
            )}
            {TABS[tabValue] === 'Explore with AI' && <HealthChat project={project} />}
          </>
        )}

      </div>
    </div>
  );
};

export default ShowProject;
